<template>
  <div class="staff-allocation-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Staff Allocation</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ summaryGroups.length }} Staff
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading staff allocation...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Staff Allocation</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Applied Filters -->
      <div v-if="filterSummary.length" class="report-filters mb-3">
        <span v-for="(item, index) in filterSummary" :key="index" class="mr-3">
          <strong>{{ item.label }}:</strong> {{ item.value }}
        </span>
      </div>

      <!-- Class / section wise allocation -->
      <div class="table-container">
        <table class="report-table">
          <thead>
            <tr>
              <th class="text-center align-middle" style="width: 4%">#</th>
              <th class="text-left align-middle" style="width: 18%">
                STAFF NAME
              </th>
              <th class="text-left align-middle" style="width: 13%">
                DESIGNATION
              </th>
              <th class="text-left align-middle" style="width: 13%">
                DEPARTMENT
              </th>
              <th class="text-center align-middle" style="width: 8%">CLASS</th>
              <th class="text-center align-middle" style="width: 8%">
                SECTION
              </th>
              <th class="text-left align-middle" style="width: 26%">
                SUBJECTS
              </th>
              <th class="text-center align-middle" style="width: 10%">
                CLASS TEACHER
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in displayRows"
              :key="row.key"
              :class="{
                'staff-group-start': row.isFirstSection,
                'group-even': row.groupEven,
              }"
            >
              <td class="text-center font-weight-bold">{{ row.groupNumber }}</td>
              <td class="text-left font-weight-bold">
                {{ (row.name || "-").toUpperCase() }}
              </td>
              <td class="text-left">{{ row.designation || "-" }}</td>
              <td class="text-left">{{ row.departmentName || "-" }}</td>
              <td class="text-center">{{ row.className || "-" }}</td>
              <td class="text-center">{{ row.sectionName || "-" }}</td>
              <td class="text-left">{{ row.subjects || "-" }}</td>
              <td class="text-center font-weight-bold">{{ row.classTeacher }}</td>
            </tr>
            <tr v-if="displayRows.length === 0">
              <td colspan="8" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No staff allocation records found for the selected filters</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import { buildStaffAllocationGroups } from "@/utils/staffAllocationReport";

export default {
  name: "StaffAllocationPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      db: "",
      cID: "",
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      // API status filter (the reports screen decides working / all staff).
      status: "present",
      staffRows: [],
      // Filters come from the Reports screen (no filter UI on this page).
      departmentFilters: [],
      classFilters: [],
      sectionFilters: [],
      subjectFilters: [],
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    summaryGroups() {
      return buildStaffAllocationGroups(this.staffRows, {
        departmentNames: this.departmentFilters,
        classNames: this.classFilters,
        sectionNames: this.sectionFilters,
        subjectNames: this.subjectFilters,
      });
    },
    displayRows() {
      const rows = [];
      this.summaryGroups.forEach((staff, groupIndex) => {
        staff.sections.forEach((section, sectionIndex) => {
          rows.push({
            key: `${staff.staffID}-${sectionIndex}`,
            groupNumber: groupIndex + 1,
            groupEven: groupIndex % 2 === 1,
            isFirstSection: sectionIndex === 0,
            name: staff.name,
            designation: staff.designation,
            departmentName: staff.departmentName,
            className: section.className,
            sectionName: section.sectionName,
            subjects: section.subjects,
            classTeacher: section.classTeacher,
          });
        });
      });
      return rows;
    },
    filterSummary() {
      const items = [];
      if (this.departmentFilters.length)
        items.push({
          label: "Department",
          value: this.departmentFilters.join(", "),
        });
      if (this.classFilters.length)
        items.push({ label: "Class", value: this.classFilters.join(", ") });
      if (this.sectionFilters.length)
        items.push({ label: "Section", value: this.sectionFilters.join(", ") });
      if (this.subjectFilters.length)
        items.push({ label: "Subject", value: this.subjectFilters.join(", ") });
      return items;
    },
  },
  async created() {
    await this.initReport();
  },
  methods: {
    async initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId || 0;
      this.status = query.status || "present";

      this.departmentFilters = this.parseQueryNames(query.depNames);
      this.classFilters = this.parseQueryNames(query.classNames);
      this.sectionFilters = this.parseQueryNames(query.sectionNames);
      this.subjectFilters = this.parseQueryNames(query.subjectNames);

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }
      if (!this.cID) {
        this.error = "Campus is missing. Please close and try again.";
        return;
      }

      await this.loadCampusInfo();
      await this.loadReportData();
    },
    parseQueryNames(raw) {
      return String(raw || "")
        .split("|")
        .map((name) => name.trim())
        .filter(Boolean);
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          `${this.$store.state.domain}campuses`,
          {
            params: { db: this.db, cId: this.cID },
            headers: this.authHeaders(),
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name || this.campusName;
            this.campusBranch = match.branch || match.branchName || "";
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed", e);
      }
    },
    authHeaders() {
      return {
        Authorization: `Bearer ${this.$store.state.userData.token}`,
        accept: "application/json",
      };
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
          `${this.$store.state.domain}Staff/StaffAllocation`,
          {
            params: {
              db: this.db,
              cID: this.cID,
              status: this.status || "present",
            },
            headers: this.authHeaders(),
          }
        );

        const payload = response.data;
        if (payload && payload.status === "success") {
          this.staffRows = Array.isArray(payload.data) ? payload.data : [];
        } else {
          this.staffRows = [];
          this.error =
            (payload && payload.message) ||
            "Failed to retrieve staff allocation data.";
        }
      } catch (err) {
        console.error("Staff allocation report error:", err);
        this.staffRows = [];
        this.error =
          "Failed to retrieve report data. Please ensure the API is running.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.staff-allocation-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 2rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.2rem;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.3);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.report-page {
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 3px solid #000;
  padding-bottom: 20px;

  .school-logo-img {
    height: 90px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
  }

  .report-info-line {
    font-size: 1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.report-filters {
  font-size: 0.85rem;
  color: #495057;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  table-layout: fixed;

  th,
  td {
    padding: 10px 12px;
    border: 1px solid #dee2e6;
    font-size: 0.85rem;
    vertical-align: top;
    word-break: break-word;
  }

  thead th {
    background: #2c3e50;
    color: white;
    font-weight: 700;
    text-transform: uppercase;
  }

  // One shaded block per staff member so class/section rows stay readable.
  tbody tr.group-even td {
    background-color: #f8f9fa;
  }

  tbody tr.staff-group-start td {
    border-top: 2px solid #2c3e50;
  }

  .no-data {
    color: #6c757d;
  }
}

.page-footer {
  text-align: center;
  margin-top: 2rem;
  color: #95a5a6;
  border-top: 1px solid #eee;
  padding-top: 1rem;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .staff-allocation-report {
    padding: 0 !important;
    background: white !important;
    font-family: "Outfit", "Inter", sans-serif !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .report-header-modern {
    margin-bottom: 18px !important;
    padding-bottom: 12px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 80px !important;
      max-width: 220px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.5rem !important;
    }

    .report-info-line {
      font-size: 0.95rem !important;
    }
  }

  .report-filters {
    color: #000 !important;
    margin-bottom: 8px !important;
  }

  .report-table {
    width: 100% !important;
    font-size: 8.5pt !important;
    margin-top: 10px !important;
    border-collapse: collapse !important;

    // Keep the header on every printed page and never split a row.
    thead {
      display: table-header-group;
    }

    tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    th,
    td {
      padding: 5px 7px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
      word-break: break-word !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr.group-even td {
      background: #f8f9fa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr.staff-group-start td {
      border-top: 2px solid #000 !important;
    }
  }

  .page-footer {
    margin-top: 12px !important;
    padding-top: 8px !important;
    border-top: 1px solid #000 !important;
    color: #000 !important;
  }
}
</style>
